import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardContent, CardActionArea, Button,
  Paper, Snackbar, Alert,
} from '@mui/material';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SendIcon from '@mui/icons-material/Send';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import db from '../../data/demoDatabase';
import { useAuth } from '../../context/AuthContext';

const isoDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  return new Date(d).toISOString().slice(0, 10);
};

const caseColumns = [
  { field: 'caseId', headerName: 'Case ID', width: 140 },
  { field: 'applicant', headerName: 'Applicant', width: 200 },
  { field: 'product', headerName: 'Product', width: 150 },
  {
    field: 'status', headerName: 'Status', width: 160,
    renderCell: (row) => <StatusBadge status={row.status} />,
  },
  { field: 'date', headerName: 'Date', width: 120 },
];

const quickActions = [
  { label: 'Submit Application', icon: SendIcon, color: '#1B365D', description: 'Submit a new referral case', path: '/partner/submit' },
  { label: 'View Cases', icon: VisibilityIcon, color: '#00B894', description: 'View all your submitted cases', path: '/partner/applications' },
  { label: 'Marketing Materials', icon: DownloadIcon, color: '#6C5CE7', description: 'Browse and share approved content', path: '/partner/marketing' },
  { label: 'View Commission', icon: MonetizationOnIcon, color: '#E17055', description: 'Check your earnings statement', path: '/partner/commission' },
];

export default function PartnerPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [refreshKey] = useState(0);

  // All cases for this partner (own + downline)
  const partnerCases = useMemo(() => {
    if (!user) return [];
    return db.cases.getForPartner(user.id, true);
    // eslint-disable-next-line
  }, [user, refreshKey]);

  // Stats computed from live db
  const stats = useMemo(() => {
    const approvedCases = partnerCases.filter((c) => ['Approved', 'Disbursed'].includes(c.status));
    const pendingCases = partnerCases.filter((c) => ['Submitted', 'In Review', 'Pending Document', 'Pending Bank Decision'].includes(c.status));
    const totalCommission = partnerCases.reduce((sum, c) => sum + (Number(c.commissionAmount) || 0), 0);
    return {
      totalCases: partnerCases.length,
      approvedCases: approvedCases.length,
      pendingCases: pendingCases.length,
      totalCommission,
    };
  }, [partnerCases]);

  // Recent cases: top 10 by createdAt desc
  const recentCases = useMemo(() => {
    return [...partnerCases]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 10)
      .map((c) => ({
        id: c.id,
        caseId: c.id,
        applicant: c.applicantName,
        product: c.productName,
        status: c.status,
        date: isoDate(c.createdAt),
      }));
  }, [partnerCases]);

  // Monthly performance: last 6 months including current, count cases per month
  const monthlyPerformance = useMemo(() => {
    const now = new Date();
    const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const buckets = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      buckets.push({
        year: d.getFullYear(),
        month: d.getMonth(),
        label: monthLabels[d.getMonth()],
        cases: 0,
      });
    }
    partnerCases.forEach((c) => {
      const d = new Date(c.createdAt);
      const b = buckets.find((x) => x.year === d.getFullYear() && x.month === d.getMonth());
      if (b) b.cases += 1;
    });
    return buckets.map(({ label, cases }) => ({ month: label, cases }));
  }, [partnerCases]);

  const partnerName = user?.name || 'Partner';
  const partnerReferralCode = user?.referralCode || '—';
  const partnerTier = user?.referralLevel || 'Silver';
  const referralLink = partnerReferralCode !== '—'
    ? `https://imoney.my/ref/${partnerReferralCode}`
    : 'https://imoney.my/ref/';

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(referralLink);
    setCopySnackbar(true);
  };

  return (
    <Box>
      {/* Welcome Banner */}
      <Paper
        sx={{
          p: 4, mb: 4, borderRadius: 3,
          background: 'linear-gradient(135deg, #1B365D 0%, #2A5298 100%)',
          color: '#fff',
          boxShadow: '0 4px 20px rgba(27,54,93,0.3)',
        }}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              Welcome back, {partnerName}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85, mb: 2 }}>
              Your referral code is displayed below. Share it with customers to earn commissions.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  px: 3, py: 1.5, borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 2 }}>
                  {partnerReferralCode}
                </Typography>
              </Box>
              <StatusBadge status={partnerTier} size="medium" />
            </Box>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right', xs: 'left' } }}>
            <Box
              sx={{
                display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                p: 2, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.1)',
              }}
            >
              <QrCode2Icon sx={{ fontSize: 80, mb: 1, opacity: 0.9 }} />
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                Scan to refer
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Cases"
            value={stats.totalCases}
            icon={FolderSharedIcon}
            subtitle="Own + downline"
            color="#1B365D"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Approved Cases"
            value={stats.approvedCases}
            icon={CheckCircleOutlineIcon}
            subtitle={stats.totalCases > 0 ? `${Math.round((stats.approvedCases / stats.totalCases) * 100)}% approval rate` : ''}
            color="#00B894"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Cases"
            value={stats.pendingCases}
            icon={HourglassEmptyIcon}
            subtitle="Awaiting processing"
            color="#F39C12"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Commission"
            value={`RM ${stats.totalCommission.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={AccountBalanceWalletIcon}
            subtitle="Across all cases"
            color="#E17055"
          />
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1B365D', mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {quickActions.map((action) => (
          <Grid item xs={12} sm={6} md={3} key={action.label}>
            <Card
              sx={{
                borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.12)' },
              }}
            >
              <CardActionArea
                sx={{ p: 2.5 }}
                onClick={() => action.path && navigate(action.path)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 48, height: 48, borderRadius: 2,
                      backgroundColor: `${action.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <action.icon sx={{ color: action.color, fontSize: 26 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1B365D' }}>
                      {action.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {action.description}
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Recent Cases Table */}
        <Grid item xs={12} lg={8}>
          {recentCases.length > 0 ? (
            <DataTable
              title="Recent Cases"
              columns={caseColumns}
              rows={recentCases}
              pageSize={10}
              searchable={false}
            />
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>No data yet</Alert>
          )}
        </Grid>

        {/* Monthly Performance Chart */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1B365D' }}>
              Monthly Performance
            </Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyPerformance}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E0E0E0" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#666' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: '#666' }} axisLine={false} tickLine={false} />
                <RechartsTooltip
                  contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                />
                <Bar dataKey="cases" fill="#1B365D" radius={[4, 4, 0, 0]} barSize={36} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Referral Link Section */}
      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1B365D' }}>
          Your Referral Link
        </Typography>
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            p: 2, borderRadius: 2, backgroundColor: '#F5F7FA',
            flexWrap: 'wrap',
          }}
        >
          <Typography
            variant="body1"
            sx={{ fontFamily: 'monospace', color: '#1B365D', fontWeight: 500, flex: 1, minWidth: 200 }}
          >
            {referralLink}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyLink}
            sx={{
              backgroundColor: '#1B365D', borderRadius: 2, textTransform: 'none', fontWeight: 600,
              '&:hover': { backgroundColor: '#142A4D' },
            }}
          >
            Copy Link
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={2500}
        onClose={() => setCopySnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopySnackbar(false)} sx={{ borderRadius: 2 }}>
          Referral link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
}
